import type { Mode } from "./types"

let preparedStream: MediaStream | null = null
let preparedMode: Mode | null = null
let preparedStreamTimer: number | undefined

export async function ensureMediaPermission(mode: Mode): Promise<void> {
  clearPreparedStream()
  preparedStream = await acquireCallMedia(mode)
  preparedMode = mode
  preparedStreamTimer = window.setTimeout(() => clearPreparedStream(), 30_000)
}

export async function requestCallMedia(mode: Mode): Promise<MediaStream> {
  if (preparedStream && preparedMode === mode && streamSupportsMode(preparedStream, mode)) {
    const stream = preparedStream
    preparedStream = null
    preparedMode = null
    if (preparedStreamTimer !== undefined) window.clearTimeout(preparedStreamTimer)
    preparedStreamTimer = undefined
    return stream
  }
  clearPreparedStream()
  return acquireCallMedia(mode)
}

async function acquireCallMedia(mode: Mode): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("media devices are unavailable")
  }
  const constraints: MediaStreamConstraints = mode === "video"
    ? { audio: true, video: defaultCameraConstraints() }
    : { audio: true }
  return navigator.mediaDevices.getUserMedia(constraints)
}

function defaultCameraConstraints(): MediaTrackConstraints {
  const portrait = typeof window !== "undefined" && window.innerHeight > window.innerWidth && Boolean(window.matchMedia?.("(pointer: coarse)").matches)
  return {
    facingMode: "user",
    width: { ideal: portrait ? 960 : 1280 },
    height: { ideal: portrait ? 1280 : 960 },
    aspectRatio: { ideal: portrait ? 3 / 4 : 4 / 3 },
    frameRate: { ideal: 30, max: 30 },
  }
}

function streamSupportsMode(stream: MediaStream, mode: Mode): boolean {
  const audioReady = stream.getAudioTracks().some((track) => track.readyState === "live")
  const videoReady = stream.getVideoTracks().some((track) => track.readyState === "live")
  return audioReady && (mode !== "video" || videoReady)
}

function clearPreparedStream(): void {
  if (preparedStreamTimer !== undefined) window.clearTimeout(preparedStreamTimer)
  preparedStreamTimer = undefined
  stopMediaStream(preparedStream)
  preparedStream = null
  preparedMode = null
}

export function setMediaStreamEnabled(stream: MediaStream | null | undefined, kind: "audio" | "video", enabled: boolean): void {
  if (!stream) return
  const tracks = kind === "audio" ? stream.getAudioTracks() : stream.getVideoTracks()
  for (const track of tracks) {
    track.enabled = enabled
  }
}

export function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return
  for (const track of stream.getTracks()) {
    track.stop()
  }
}
